import React from 'react';
import { 
  Clock, 
  CreditCard, 
  Shield, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const PaymentStatusTimeline = ({ payment, order }) => {
  const getTimelineSteps = () => {
    const steps = [
      {
        id: 'initiated',
        title: 'Payment Initiated',
        description: 'Payment request created and submitted to gateway',
        time: payment?.created_at,
        icon: Clock,
        completed: true
      },
      {
        id: 'processing',
        title: 'Processing Payment',
        description: 'Payment being verified and processed by gateway',
        time: payment?.created_at,
        icon: CreditCard,
        completed: ['processing', 'completed', 'failed', 'refunded']?.includes(payment?.status)
      },
      {
        id: 'verification',
        title: 'Security Verification',
        description: 'Payment security checks and fraud detection',
        time: payment?.updated_at,
        icon: Shield,
        completed: ['completed', 'failed', 'refunded']?.includes(payment?.status),
        current: payment?.status === 'processing'
      },
      {
        id: 'final',
        title: getStatusTitle(payment?.status),
        description: getStatusDescription(payment?.status),
        time: payment?.processed_at || payment?.updated_at,
        icon: getStatusIcon(payment?.status),
        completed: ['completed', 'failed', 'refunded']?.includes(payment?.status),
        current: payment?.status === 'completed'
      }
    ];

    return steps;
  };

  function getStatusTitle(status) {
    const titles = {
      completed: 'Payment Completed',
      failed: 'Payment Failed',
      refunded: 'Payment Refunded',
      cancelled: 'Payment Cancelled',
      pending: 'Awaiting Confirmation',
      processing: 'Processing...'
    };
    return titles?.[status] || 'Payment Status';
  }

  function getStatusDescription(status) {
    const descriptions = {
      completed: 'Payment successfully processed and confirmed',
      failed: 'Payment could not be processed',
      refunded: 'Payment has been refunded to your account',
      cancelled: 'Payment was cancelled by user or system',
      pending: 'Waiting for final confirmation',
      processing: 'Payment is being processed'
    };
    return descriptions?.[status] || 'Payment status update';
  }

  function getStatusIcon(status) {
    switch (status) {
      case 'completed':
        return CheckCircle;
      case 'failed': case'cancelled':
        return XCircle;
      case 'refunded':
        return RefreshCw;
      case 'processing':
        return CreditCard;
      default:
        return AlertTriangle;
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp)?.toLocaleString('en-ZA', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStepColor = (step) => {
    if (step?.current) return 'blue';
    if (step?.completed) {
      if (payment?.status === 'failed' || payment?.status === 'cancelled') {
        return 'red';
      }
      if (payment?.status === 'refunded') {
        return 'purple';
      }
      return 'green';
    }
    return 'gray';
  };

  const steps = getTimelineSteps();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Payment Progress</h3>
        {payment?.gateway_reference && (
          <span className="text-sm text-gray-500">
            Ref: {payment?.gateway_reference}
          </span>
        )}
      </div>
      <div className="space-y-8">
        {steps?.map((step, index) => {
          const IconComponent = step?.icon;
          const color = getStepColor(step);
          const colorClasses = {
            blue: 'text-blue-600 bg-blue-100 border-blue-200',
            green: 'text-green-600 bg-green-100 border-green-200',
            red: 'text-red-600 bg-red-100 border-red-200',
            purple: 'text-purple-600 bg-purple-100 border-purple-200',
            gray: 'text-gray-400 bg-gray-100 border-gray-200'
          };

          return (
            <div key={step?.id} className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${colorClasses?.[color]}`}>
                  <IconComponent className="h-5 w-5" />
                </div>
                {index < steps?.length - 1 && (
                  <div className={`w-0.5 h-8 mt-2 ml-4 ${
                    step?.completed ? 'bg-green-300' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className={`text-sm font-medium ${
                    step?.completed || step?.current ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step?.title}
                  </h4>
                  {step?.time && (
                    <span className="text-xs text-gray-500">
                      {formatTime(step?.time)}
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-1 ${
                  step?.completed || step?.current ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step?.description}
                </p>
                
                {step?.current && (
                  <div className="mt-2">
                    <div className="flex items-center text-xs text-blue-600">
                      <div className="animate-pulse w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                      Current step
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Estimated completion time */}
      {(payment?.status === 'pending' || payment?.status === 'processing') && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-blue-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-blue-900">Estimated Completion</p>
              <p className="text-sm text-blue-700">
                Payments typically complete within 2-5 minutes. You will receive email 
                confirmation once processing is complete.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Gateway information */}
      {payment?.payment_gateway && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Gateway Information</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Payment Gateway:</span>
              <span className="ml-2 font-medium capitalize">
                {payment?.payment_gateway?.replace('_', ' ')}
              </span>
            </div>
            {payment?.gateway_transaction_id && (
              <div>
                <span className="text-gray-600">Transaction ID:</span>
                <span className="ml-2 font-mono text-xs">
                  {payment?.gateway_transaction_id}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentStatusTimeline;